import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { signInInvestor, verifyCode } from '../../reducers/authReducer';
import { RootState } from '../../store/store';
import { Email, Lock } from '@mui/icons-material';
import { Box, Container, Typography, Link } from '@mui/material';
import {  INACTIVE_USER, VALIDATION_ERROR_REQUIRED_MESSAGE } from '../../constants/actions';
import { SignInFormStyles } from '../../assets/css/signInForm.styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import themeAr from "../../themes/index.ar";
import themeEn from "../../themes/index.en";
import { useAppDispatch } from '../../hooks/useAppDispatch';
import CustomLabeledTextField from '../../components/customTextField/customLabeledTextField';
import CustomButton from '../../components/customButton/customButton';
import Loader from '../../components/loader';
import { useTranslation } from 'react-i18next';
import { sendActionStatus } from '../../reducers/externalApiReducer';
import { InvestorStatus } from '../../constants/enums';

const InvestorSignInForm: React.FC = () => {
  const { t }: { t: (key: string) => string } = useTranslation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const isRTL = currentLanguage === 'ar'; 

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [codePopupOpen, setCodePopupOpen] = useState(false);
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false, code:false });
  const authState = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value);
  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPassword(event.target.value);
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCode(event.target.value);

  const validate = () => {
    setTouched({ email: true, password: true, code:false });
    return email !== '' && password !== '';
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (validate()) { 
      dispatch(signInInvestor({ userName: email, password, rememberMe })).unwrap()  .then((response) => { 
        const { token, messageCode, success,isCodeRequired, status, investorId } = response; 
        if(!success){
          dispatch(sendActionStatus({token:'', status:false, action:'login',message:`${t(messageCode || "")}` ,redirection:'' }));  
        }
        else if(isCodeRequired){
            setCodePopupOpen(true);
        } else if(status && status === InvestorStatus.Inactive){ 
          dispatch(sendActionStatus({token:'', status:false, action:'login',message:`${t(messageCode || "")}` ,redirection:'' }));  
        } else if(success && token && investorId){ 
            switch (status) {
                case InvestorStatus.Draft: 
                  dispatch(sendActionStatus({token, status:true, action:'register',redirection:`/Registration/${investorId}` }));
                  break;
                case InvestorStatus.PendingApproval: 
                  dispatch(sendActionStatus({token, status:true, action:'profile',redirection:`/Profile/${investorId}/View` }));
                  break;
                case InvestorStatus.Active: 
                    dispatch(sendActionStatus({token, status:true, action:'profile',redirection:`/Profile/${investorId}` })); 
                    break;
                default:
                  break;
              } 
        }
        else {  
          dispatch(sendActionStatus({token:'', status:false, action:'login',message:`${t(messageCode || "")}`, redirection:'' }));  
        } 
      }).catch((error) => {
        console.error("Failed to save item:", error);
      });
    } else {
      toast.error('Please fill in all required fields.');
    }
  };

  const handleRegisterClick = () => {  
    dispatch(sendActionStatus({token: "", status:true, action:'register',redirection:`/Registration` }));
};

  const handleForgotPasswordClick = () => {  
    dispatch(sendActionStatus({token: "", status:true, action:'forgotPassword',redirection:`/ForgotPassword` }));
};

  const handleVerfiyCode = () => {
    if(code.trim() === ""){
      setTouched({ email: false, password: false, code:true });
      return;
    }
    dispatch(verifyCode({ code, email })).unwrap()  .then((response) => { 
      const { token, messageCode, success,isCodeRequired, status, investorId } = response; 
      if(!success){
        dispatch(sendActionStatus({token:'', status:false, action:'login',message: `${t(messageCode)}` ,redirection:'' }));  
      }
      else if(isCodeRequired){
          setCodePopupOpen(true);
      } else if(status && status === InvestorStatus.Inactive){ 
        dispatch(sendActionStatus({token:'', status:false, action:'login',message:`${t(messageCode)}` ,redirection:'' }));  
      } else if(success && token && investorId){ 
          switch (status) {
              case InvestorStatus.Draft: 
                dispatch(sendActionStatus({token, status:true, action:'register',redirection:`/Registration/${investorId}` }));
                break;
              case InvestorStatus.PendingApproval: 
                dispatch(sendActionStatus({token, status:true, action:'profile',redirection:`/Profile/${investorId}/View` }));
                break;
              case InvestorStatus.Active: 
                  dispatch(sendActionStatus({token, status:true, action:'profile',redirection:`/Profile/${investorId}` })); 
                  break;
              default:
                break;
            } 
      }
      else {  
        dispatch(sendActionStatus({token:'', status:false, action:'login',message: `${t(messageCode)}`, redirection:''}));  
      } 
    }).catch((error) => {
      console.error("Failed to save item:", error);
    });
  };

  return ( 
    <ThemeProvider theme={isRTL ? themeAr : themeEn}>
    <Container component="main"  sx={{ ...SignInFormStyles.container, textAlign: isRTL ? 'right' : 'left', }}> 
      {authState.isLoading && <Loader />}  
        <>
        {codePopupOpen ? (
          <>
            <Typography variant="h4" component="h4" align="center" sx={{ marginBottom:"25px", fontWeight: 'bold' }}>
                {t('codeRequired')}
            </Typography>  
            <Typography  variant="h4" component="h4" align="center" sx={{ marginBottom:"25px", fontWeight: 'bold' }}> 
                {t('codeMessage')}
            </Typography>  
            <Box
            component="form"
            onSubmit={handleVerfiyCode}
            noValidate
            sx={SignInFormStyles.form}
            >
              <CustomLabeledTextField
              label={`${t('code')}`}
              name="code"
              type="text"
              value={code}
              icon={<Email />}
              onChange={handleCodeChange}
              required
              autoFocus
              isRTL={isRTL}
              error={touched.code && code === ''}
              helperText={
                  touched.code && code === ''
                  ? VALIDATION_ERROR_REQUIRED_MESSAGE
                  : ''
              }
              /> 
            <CustomButton
              type="submit" 
              style={SignInFormStyles.button}
              // startIcon={<VerifiedIcon/>} 
            > 
              {t('verify')}
            </CustomButton>
        </Box>  
        </>
        ) : (
        <>
          <Typography variant="h2" component="h2" align="center" sx={{ marginBottom:"25px", fontWeight: 'bold' }}>
              {t('login')}
          </Typography>  
          <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={SignInFormStyles.form}
          >
              <CustomLabeledTextField
              label={`${t('email')}`}
              name="email"
              type="email"
              value={email}
              icon={<Email />}
              onChange={handleEmailChange}
              required
              autoFocus
              error={touched.email && email === ''}
              isRTL={isRTL}
              helperText={
                  touched.email && email === ''
                  ? VALIDATION_ERROR_REQUIRED_MESSAGE
                  : ''
              }
              />
              <CustomLabeledTextField
              label={`${t('password')}`}
              name="password"
              type="password"
              value={password}
              icon={<Lock />}
              onChange={handlePasswordChange}
              required
              isRTL={isRTL}
              error={touched.password && password === ''}
              helperText={
                  touched.password && password === ''
                  ? VALIDATION_ERROR_REQUIRED_MESSAGE
                  : ''
              }
              /> 
              <CustomButton
              type="submit"
              disabled={authState.isLoading}
              style={
                  authState.isLoading
                  ? SignInFormStyles.disableButton
                  : SignInFormStyles.button
              }
              > 
              {authState.isLoading ? `${t('loggingIn')}` : `${t('logIn')}`}
              </CustomButton>
          </Box> 
          {isRTL?(
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ marginTop: '15px' }}> 
            <Link
                  onClick={handleRegisterClick}
                  sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  }}
              > 
                  {t('registerHere')}
              </Link>
              <Typography sx={{ fontWeight: 'bold', marginLeft: '8px' }}>
                  {t('newInvestor')}
              </Typography>
          </Box>   
          ):(
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ marginTop: '15px' }}>
              <Typography sx={{ fontWeight: 'bold', marginRight: '8px' }}>
                  {t('newInvestor')}
              </Typography>
              <Link
                  onClick={handleRegisterClick}
                  sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  }}
              > 
                  {t('registerHere')}
              </Link>
          </Box>   
          )}  
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ marginTop: '15px' }}>
                <Link   
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    }} 
                  onClick={handleForgotPasswordClick}
                  >
                      {t('forgotPassword')}
                  </Link> 
          </Box>
        </>
        )} 
        </>
    </Container> 
    </ThemeProvider>
  );
};

export default InvestorSignInForm;
